import React from "react";
import SEO from "../components/SEO";
import { Link } from "react-router-dom";

const TIERS = [
  { name: "Patient", points: "0 – 499 pts", perks: ["1 pt / $1 spent", "Birthday gift", "Early drop access"] },
  { name: "Member", points: "500 – 1,999 pts", perks: ["1.25 pts / $1 spent", "Members-only pricing", "Free delivery upgrades"] },
  { name: "Circle", points: "2,000+ pts", perks: ["1.5 pts / $1 spent", "Invites to private events", "Concierge patient specialist"] },
];

export default function Rewards() {
  return (
    <>
      <SEO
        title="Rewards · District Wellness Center Loyalty Program"
        description="Earn points on every purchase at District Wellness Center. Unlock members-only pricing, early drops, and private events."
        path="/rewards"
        breadcrumbs={[{ name: "Rewards", path: "/rewards" }]}
      />
      <section className="bg-ink text-white relative">
        <div className="grain absolute inset-0 opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 sm:py-32">
          <div className="max-w-3xl">
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-5">DWC · Rewards</div>
            <h1 className="font-serif text-5xl sm:text-7xl leading-[0.95] tracking-tight">
              The DWC <em className="text-brand">Circle</em>.
            </h1>
            <p className="mt-6 text-white/70 text-lg leading-relaxed">
              Every dollar earns a point. Points unlock members-only pricing, private drops, and concierge patient support.
              Free to join — across all three DWC locations.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-5">
          {TIERS.map((t, i) => (
            <article key={t.name} className={`p-10 border border-black/10 ${i === 2 ? "bg-ink text-white" : "bg-white"}`} data-testid={`tier-${t.name.toLowerCase()}`}>
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-4">Tier 0{i+1}</div>
              <h3 className="font-serif text-4xl leading-tight">{t.name}</h3>
              <div className={`text-[11px] uppercase tracking-[0.25em] mt-2 ${i === 2 ? "text-white/60" : "text-ink-muted"}`}>{t.points}</div>
              <ul className="mt-8 space-y-3 text-sm">
                {t.perks.map((p) => (
                  <li key={p} className="flex gap-3"><span className="text-brand">✦</span> {p}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
        <div className="mt-16 text-center">
          <Link to="/locations" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-600 text-white px-7 py-4 text-[12px] uppercase tracking-[0.22em]" data-testid="rewards-join-btn">
            Join at your nearest DWC →
          </Link>
        </div>
      </section>

      {/* HOW THE PROGRAM WORKS — long-form for SEO */}
      <section className="py-24 bg-[#FAFAFA] border-t border-black/10">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-5">How it works</div>
          <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight">
            The DC medical cannabis rewards program, in plain English.
          </h2>
          <div className="mt-8 space-y-5 text-ink-muted text-lg leading-relaxed">
            <p>
              District Wellness Center is a licensed medical cannabis dispensary in Washington, DC — 4915 Georgia
              Ave NW, in the heart of Petworth. The DWC Circle is our free loyalty program for DC patients and
              out-of-state visitors who&rsquo;ve registered on the myABCA portal. You don&rsquo;t need to pay to
              join, you don&rsquo;t need a special card, and you can start earning on your first visit.
            </p>
            <p>
              Every dollar you spend at DWC — whether you walk into 4915 Georgia Ave NW, order for same-day
              cannabis delivery across DC, or reserve online from our Flowhub-synced menu — earns you points
              toward members-only pricing and early access to small-batch drops (Alt Sol, Khalifa Kush, and
              rotating DC-cultivator brands).
            </p>
          </div>

          <h3 className="font-serif text-2xl text-ink mt-14 mb-4">Earning points across DC</h3>
          <ul className="space-y-3 text-lg text-ink-muted">
            <li className="flex gap-3"><span className="text-brand">✦</span> Walk-ins welcome at 4915 Georgia Ave NW — earn from your very first jar.</li>
            <li className="flex gap-3"><span className="text-brand">✦</span> Same-day delivery across Washington, DC — Petworth, Columbia Heights, Shaw, Adams Morgan, Dupont Circle, Georgetown, Capitol Hill, Navy Yard and every DC neighborhood we serve.</li>
            <li className="flex gap-3"><span className="text-brand">✦</span> Pre-order pickup — reserve on the live menu, earn on arrival.</li>
          </ul>

          <h3 className="font-serif text-2xl text-ink mt-12 mb-4">Redeeming perks</h3>
          <p className="text-lg text-ink-muted leading-relaxed">
            Members-only pricing is applied at checkout automatically. Circle-tier patients get first look at
            private events, new cultivator drops, and concierge patient-specialist appointments. Every tier
            includes a birthday gift and delivery upgrades when they&rsquo;re available.
          </p>

          <h3 className="font-serif text-2xl text-ink mt-12 mb-4">Signing up</h3>
          <p className="text-lg text-ink-muted leading-relaxed">
            Ask any patient specialist at the register or the delivery driver at your door. You&rsquo;ll need
            to be 21+, DC-registered on myABCA, and have a valid photo ID. That&rsquo;s it. No email
            gatekeeping, no app to install.
          </p>

          {/* Related links */}
          <div className="mt-16 pt-10 border-t border-black/10 grid sm:grid-cols-3 gap-6">
            <Link to="/menu" className="group block" data-testid="rewards-related-menu">
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-2">Related</div>
              <div className="font-serif text-xl text-ink group-hover:text-brand">Shop the live menu →</div>
            </Link>
            <Link to="/delivery" className="group block" data-testid="rewards-related-delivery">
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-2">Related</div>
              <div className="font-serif text-xl text-ink group-hover:text-brand">Same-day DC delivery →</div>
            </Link>
            <Link to="/blog/best-cannabis-dispensary" className="group block" data-testid="rewards-related-blog">
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-2">Related</div>
              <div className="font-serif text-xl text-ink group-hover:text-brand">Best DC dispensary guide →</div>
            </Link>
          </div>
        </div>
      </section>

      {/* Google review CTA */}
      <section className="bg-ink text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05]">
            Already a Circle member? <em className="text-brand">Leave us a Google review.</em>
          </h2>
          <p className="mt-5 text-white/70 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
            Honest reviews help other DC patients find us and help our neighborhood dispensary grow.
            Ask any patient specialist about today&rsquo;s patient-appreciation promotion — no incentives in
            exchange for reviews, just gratitude.
          </p>
          <div className="mt-10">
            <a
              href="https://maps.app.goo.gl/ynJcSrirV4qpH1gz8"
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 bg-brand hover:bg-brand-600 text-white px-7 py-4 text-[11px] uppercase tracking-[0.22em]"
              data-testid="rewards-google-review"
            >
              Leave a Google review →
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
